import axios, { type AxiosProgressEvent } from 'axios';

import { storageApi } from '@/api';

import type { VStorageProgress, VStorageSpecificCommandApi } from './type';

export type RequestForExternalParameter = {
  body?: Record<string, any> | Blob | BufferSource;
  headers?: Record<string, string>;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  pathParams?: string;
  querystring?: string[][] | Record<string, any> | string | URLSearchParams;
  responseType?: 'json' | 'text' | 'blob' | 'stream' | 'arraybuffer';
  url: string;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void;
};

export const getPresignURLList = (
  bucket: string,
  entryList: { key: string; option?: { isDir?: boolean } }[],
) => {
  const fileList = entryList.map(({ key, option }) => ({
    path: makeKey(key, option).replace(/^\//, ''),
    size: 0,
  }));

  return storageApi
    .createPresignURL({
      bucket,
      fileList,
    })
    .then(res => res.data.resultData);
};

export const makeKey = (
  key: string,
  option?: { bucket?: string; isDir?: boolean; isExistPath?: boolean },
) => {
  const { bucket, isDir, isExistPath } = option ?? {
    bucket: null,
    isDir: false,
    isExistPath: false,
  };
  const result =
    (bucket ? `${bucket}` : '') +
    `${isExistPath ? '' : '/'}${key}` +
    (isDir ? '/' : '');

  return result.replace(/\/+/g, '/');
};

export const requestForExternal = <T = any>({
  body,
  headers,
  method = 'GET',
  pathParams = '',
  querystring,
  url,
  responseType = 'json',
  onUploadProgress,
  onDownloadProgress,
}: RequestForExternalParameter) => {
  const strQuerystring = `${new URLSearchParams(querystring)}`;
  const fullUrl = `${url}${pathParams}${strQuerystring ? `?${strQuerystring}` : ''}`;

  return axios({
    url: fullUrl,
    method,
    headers: {
      ...headers,
    },
    data: body,
    responseType,
    onUploadProgress,
    onDownloadProgress,
  }).then(res => ({
    status: res.status,
    statusText: res.statusText,
    headers: res.headers,
    data: res.data as T,
  }));
};

export const vStorageProgressPipe = (
  callback?: (progress: VStorageProgress) => void,
) => {
  return callback
    ? (event: AxiosProgressEvent) =>
        callback?.({
          total: event.total ?? 0,
          loaded: event.loaded ?? 0,
          rate: event.rate ?? 0,
        })
    : undefined;
};

export const GET_DEFAULT_SPECIFIC_COMMAND_API = () => {
  const command = {
    bucket: '',
    downloadDirectory: ({ key, downloadCallback }) =>
      storageApi
        .downloadDirectory(
          { bucket: command.bucket, filepath: key },
          {
            onDownloadProgress(event) {
              downloadCallback?.({
                loaded: event.loaded,
                rate: event.rate ?? 0,
                total: event.total ?? 0,
              });
            },
          },
        )
        .then(() => {
          const fileName = `${key.replace(/\/$/, '').split('/').pop()}.zip`;

          return { fileName };
        }),

    getList: ({ limit, prefix, startAfter, ...rest }) =>
      storageApi
        .getList({
          bucket: command.bucket,
          limit: limit ?? 100,
          prefix,
          startAfter: startAfter ?? '',
          ...rest,
        })
        .then(res => res.data.resultData),

    remove: ({ key }) =>
      storageApi
        .remove({ bucket: command.bucket, filepath: key.replace(/^\//, '') })
        .then(res => res.status === 200 || res.status === 204)
        .catch(err => {
          console.warn(err);
          return false;
        }),
  } as VStorageSpecificCommandApi;

  return command;
};
